/**
 * Resolving the client-configurable accent color into the app's brand tonalities.
 *
 * The SDK and the public API expose a single `accentColor` (per-app, stored on `apps.accent_color`,
 * or pushed live via the SDK `customize` postMessage). Every brand *tonality* the UI needs — the
 * shades behind selectors, filters, inputs, logos and icons — is DERIVED from that one color here,
 * rather than hardcoded. `app.css` keeps the teal defaults as the no-JS / pre-hydration fallback;
 * at runtime the resolved custom properties override the same vars with shades of the chosen color.
 *
 * `accentThemeVars` returns those custom properties; `__root` renders them as a `:root` `<style>` in
 * the SSR HTML (see accent-prepaint) so there's no teal flash before hydration.
 *
 * The derivation reproduces the historical hardcoded palette exactly when given the brand teal
 * (`#33ccaa`): `-200`/`-less-darker` are multiplicative *shades* (toward black), `-lighter`/
 * `-more-lighter`/`-lightest` are *tints* (toward white). See app.css lines ~256-272.
 *
 * On-accent text (`--primary-foreground`, `--accent-bg-text-color`): for the DEFAULT teal we leave
 * app.css's mode-specific values untouched (overriding them flat would regress dark-mode buttons).
 * For a CUSTOM accent we set a contrast-correct value (same in light & dark — the accent itself is
 * mode-independent), so e.g. a dark purple gets white text instead of app.css's dark-mode dark text.
 */

const DEFAULT_ACCENT = '#33ccaa' // brand teal — equals the '#3ca' store/SDK default

/** Expand `#rgb` → `#rrggbb`, l-case, prefix `#`. Returns null for anything unparseable. */
export function normalizeHex(input: string | null | undefined): string | null {
  if (!input) return null
  let hex = input.trim().toLowerCase()
  if (!hex.startsWith('#')) hex = `#${hex}`
  if (/^#[0-9a-f]{3}$/.test(hex)) hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
  return /^#[0-9a-f]{6}$/.test(hex) ? hex : null
}

type Rgb = readonly [number, number, number]

function hexToRgb(hex: string): Rgb {
  const v = Number.parseInt(hex.slice(1), 16)
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255]
}

/** Multiply toward black: `t=0.12` → 88% brightness. */
const shade = (rgb: Rgb, t: number): Rgb => rgb.map((c) => c * (1 - t)) as unknown as Rgb

/** Mix toward white: `t=0.85` → 85% of the way to #fff. */
const tint = (rgb: Rgb, t: number): Rgb => rgb.map((c) => c + (255 - c) * t) as unknown as Rgb

/** Trim float noise so `99.96000000000001` serializes as `99.96`. */
const fmt = (n: number) => Number(n.toFixed(2)).toString()

const triplet = (rgb: Rgb) => rgb.map(fmt).join(' ')
const rgbFn = (rgb: Rgb) => `rgb(${triplet(rgb)})`

/** Linearize one 0-255 sRGB channel (WCAG). Shared with color-field's contrast math. */
export function srgbChannelToLinear(c: number): number {
  const s = c / 255
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

/** WCAG relative luminance, 0 (black) … 1 (white). */
function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex)
  return 0.2126 * srgbChannelToLinear(r) + 0.7152 * srgbChannelToLinear(g) + 0.0722 * srgbChannelToLinear(b)
}

/** Does the accent need dark text on top? Threshold L>0.179 is the black-vs-white WCAG cross-over. */
export function prefersDarkText(hex: string): boolean {
  return luminance(hex) > 0.179
}

/**
 * The CSS custom properties a given accent color resolves to. Set on `<html>` so both light and
 * dark mode (which reference the same `--color-primary*` triplets) inherit them. Invalid/empty
 * input falls back to the brand teal — yielding exactly the app.css defaults (zero visual change),
 * including leaving the mode-specific on-accent text colors to app.css.
 */
export function accentThemeVars(input: string | null | undefined): Record<string, string> {
  const hex = normalizeHex(input) ?? DEFAULT_ACCENT
  const base = hexToRgb(hex)
  const vars: Record<string, string> = {
    '--primary': hex,
    // SVG logos/icons + selectors read these as `rgb(var(--…))` triplets.
    '--color-primary': triplet(base),
    '--color-primary-rgb': triplet(base),
    '--color-accent-rgb': triplet(base),
    '--color-primary-200': triplet(shade(base, 0.12)),
    '--color-primary-less-darker': rgbFn(shade(base, 0.06)),
    '--color-primary-lighter': rgbFn(tint(base, 0.12)),
    '--color-primary-more-lighter': rgbFn(tint(base, 0.24)),
    '--color-primary-lightest': rgbFn(tint(base, 0.85)),
  }
  // Only override on-accent text for a custom accent; the default defers to app.css (see header).
  if (hex !== DEFAULT_ACCENT) {
    const dark = prefersDarkText(hex)
    vars['--primary-foreground'] = dark ? 'oklch(0.205 0 0)' : 'oklch(0.985 0 0)'
    vars['--accent-bg-text-color'] = dark ? '#000' : '#fff'
  }
  return vars
}
